// src/pages/AdminAffiliateSection.tsx
// ============================================================================
// ADIM 10: Satış Ortaklığı - Admin Sayfası
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Users,
  DollarSign,
  Clock,
  Key,
  FileText,
  Check,
  X,
  ChevronRight,
  ChevronDown,
  Search,
  Edit2,
  Trash2,
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowRightCircle,
} from 'lucide-react';
import {
  getAdminAffiliateStats,
  getAdminPendingPayments,
  getAdminReferralCodes,
  getAdminPaymentHistory,
  approveAffiliatePayment,
  bulkApproveAffiliatePayments,
  carryOverAffiliatePayment,
  deleteReferralCode,
  updateReferralCode,
  getAffiliateReferredUsers,
  AdminAffiliateStats,
  PendingPayment,
  ReferralCode,
  PaymentHistoryUser,
  PaymentHistoryItem,
  formatCurrency,
  formatDate,
  formatShortDate,
} from '../services/affiliate-api';
import toast from 'react-hot-toast';
import CarryOverConfirmModal from '../components/admin/CarryOverConfirmModal';

export default function AdminAffiliateSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'payments' | 'codes' | 'history'>('payments');

  // Data states
  const [stats, setStats] = useState<AdminAffiliateStats | null>(null);
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [referralCodes, setReferralCodes] = useState<ReferralCode[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryUser[]>([]);

  // UI states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PendingPayment | null>(null);
  const [selectedCode, setSelectedCode] = useState<ReferralCode | null>(null);
  const [transactionRef, setTransactionRef] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [newCode, setNewCode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Devir (carry-over) — asgari ödeme tutarının altında kalan dönemler
  const [carryingOver, setCarryingOver] = useState<string | null>(null);
  const [carryOverModal, setCarryOverModal] = useState<PendingPayment | null>(null);

  // Dönem odaklı görünüm + toplu ödeme (Ödeme Yönetimi ekranıyla aynı düzen)
  const [selectedAffPeriodKey, setSelectedAffPeriodKey] = useState<string | null>(null);
  const [selectedAffRows, setSelectedAffRows] = useState<Set<string>>(new Set());
  const [affSearch, setAffSearch] = useState('');
  const [affHideBelowMin, setAffHideBelowMin] = useState(false);
  const [affBulkModalOpen, setAffBulkModalOpen] = useState(false);
  const [affBulkRef, setAffBulkRef] = useState('');
  const [affBulkNote, setAffBulkNote] = useState('');
  const [affBulkSubmitting, setAffBulkSubmitting] = useState(false);
  const [selectedHistoryKey, setSelectedHistoryKey] = useState<string | null>(null);
  const [historySearch, setHistorySearch] = useState('');

  // Getirilen kullanıcılar modalı
  const [showReferredModal, setShowReferredModal] = useState(false);
  const [referredUsers, setReferredUsers] = useState<any[]>([]);
  const [loadingReferred, setLoadingReferred] = useState(false);
  const [referredForCode, setReferredForCode] = useState<ReferralCode | null>(null);

  const openReferredUsers = async (code: ReferralCode) => {
    setReferredForCode(code);
    setShowReferredModal(true);
    setLoadingReferred(true);
    try {
      const data = await getAffiliateReferredUsers(code.id);
      setReferredUsers(data);
    } catch {
      toast.error('Getirilen kullanıcılar yüklenemedi');
      setReferredUsers([]);
    } finally {
      setLoadingReferred(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, paymentsData, codesData, historyData] = await Promise.all([
        getAdminAffiliateStats(),
        getAdminPendingPayments(),
        getAdminReferralCodes(),
        getAdminPaymentHistory(),
      ]);

      setStats(statsData);
      setPendingPayments(paymentsData);
      setReferralCodes(codesData);
      setPaymentHistory(historyData);
    } catch (err) {
      setError('Veriler yüklenirken bir hata oluştu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Ödeme Onaylama
  const handleApprovePayment = async () => {
    if (!selectedPayment) return;

    try {
      setIsSubmitting(true);
      await approveAffiliatePayment({
        userId: selectedPayment.userId,
        year: selectedPayment.year,
        month: selectedPayment.month,
        transactionReference: transactionRef || undefined,
        paymentNote: paymentNote || undefined,
      });

      toast.success('Ödeme onaylandı!');
      setShowApproveModal(false);
      setTransactionRef('');
      setPaymentNote('');
      setSelectedPayment(null);
      fetchAllData(); // Refresh
    } catch (err: any) {
      toast.error(err.message || 'Ödeme onaylanamadı');
    } finally {
      setIsSubmitting(false);
    }
  };

  // =====================================================================
  // DÖNEM ODAKLI GÖRÜNÜM — backend düz liste döner, admin dönem dönem çalışır
  // =====================================================================
  const affPeriodGroups = React.useMemo(() => {
    const map = new Map<string, { year: number; month: number; periodName: string; paymentWindowStart: string; rows: PendingPayment[] }>();
    pendingPayments.forEach((p) => {
      const key = `${p.year}-${p.month}`;
      if (!map.has(key)) {
        map.set(key, { year: p.year, month: p.month, periodName: p.periodName, paymentWindowStart: p.paymentWindowStart, rows: [] });
      }
      map.get(key)!.rows.push(p);
    });
    return Array.from(map.entries())
      .map(([key, g]) => {
        const windowEnd = new Date(g.paymentWindowStart);
        windowEnd.setDate(windowEnd.getDate() + 5);
        return {
          key,
          ...g,
          userCount: g.rows.length,
          totalPayable: Math.round(g.rows.reduce((s, r) => s + (r.payableAmount ?? r.commission), 0) * 100) / 100,
          belowMinCount: g.rows.filter((r) => r.isBelowMinimum).length,
          isOverdue: new Date() > windowEnd,
          // Ödeme penceresi henüz açılmadıysa dönem "sıradaki" olarak gösterilir
          windowOpen: g.rows.some((r) => r.paymentWindowOpen !== false),
        };
      })
      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [pendingPayments]);

  useEffect(() => {
    if (affPeriodGroups.length > 0 && !affPeriodGroups.some((g) => g.key === selectedAffPeriodKey)) {
      setSelectedAffPeriodKey(affPeriodGroups[0].key);
      setSelectedAffRows(new Set());
    }
  }, [affPeriodGroups, selectedAffPeriodKey]);

  const activeAffGroup = affPeriodGroups.find((g) => g.key === selectedAffPeriodKey) ?? null;
  const affRowKey = (p: PendingPayment) => `${p.userId}-${p.year}-${p.month}`;

  const visibleAffRows = (activeAffGroup?.rows ?? [])
    .filter((p) =>
      p.userName.toLowerCase().includes(affSearch.toLowerCase()) ||
      p.userEmail.toLowerCase().includes(affSearch.toLowerCase()) ||
      p.referralCode.toLowerCase().includes(affSearch.toLowerCase())
    )
    .filter((p) => !affHideBelowMin || !p.isBelowMinimum)
    .sort((a, b) => (b.payableAmount ?? b.commission) - (a.payableAmount ?? a.commission));

  const selectableAffRows = visibleAffRows.filter((p) => (p.payableAmount ?? p.commission) > 0);
  const allAffSelected = selectableAffRows.length > 0 && selectableAffRows.every((p) => selectedAffRows.has(affRowKey(p)));

  const toggleAffRow = (p: PendingPayment) => {
    const key = affRowKey(p);
    setSelectedAffRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAllAff = () => {
    setSelectedAffRows(allAffSelected ? new Set() : new Set(selectableAffRows.map(affRowKey)));
  };

  const selectedAffTotal = visibleAffRows
    .filter((p) => selectedAffRows.has(affRowKey(p)))
    .reduce((s, p) => s + (p.payableAmount ?? p.commission), 0);

  // Ödeme geçmişi de dönem dönem gruplanır
  const affHistoryGroups = React.useMemo(() => {
    type Row = { user: PaymentHistoryUser; payment: PaymentHistoryItem };
    const map = new Map<string, { year: number; month: number; periodName: string; rows: Row[] }>();
    paymentHistory.forEach((user) => {
      user.payments.forEach((payment) => {
        const key = `${payment.year}-${payment.month}`;
        if (!map.has(key)) {
          map.set(key, { year: payment.year, month: payment.month, periodName: payment.periodName, rows: [] });
        }
        map.get(key)!.rows.push({ user, payment });
      });
    });
    return Array.from(map.entries())
      .map(([key, g]) => ({
        key,
        ...g,
        userCount: g.rows.length,
        total: Math.round(g.rows.reduce((s, r) => s + r.payment.commission, 0) * 100) / 100,
        carriedCount: g.rows.filter((r) => (r.payment.carriedPeriods?.length ?? 0) > 0).length,
        lastPaidAt: g.rows.map((r) => r.payment.paidAt).sort().slice(-1)[0],
      }))
      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [paymentHistory]);

  useEffect(() => {
    if (affHistoryGroups.length > 0 && !affHistoryGroups.some((g) => g.key === selectedHistoryKey)) {
      setSelectedHistoryKey(affHistoryGroups[0].key);
    }
  }, [affHistoryGroups, selectedHistoryKey]);

  const activeAffHistoryGroup = affHistoryGroups.find((g) => g.key === selectedHistoryKey) ?? null;

  const visibleAffHistoryRows = (activeAffHistoryGroup?.rows ?? [])
    .filter((r) =>
      r.user.userName.toLowerCase().includes(historySearch.toLowerCase()) ||
      r.user.userEmail.toLowerCase().includes(historySearch.toLowerCase()) ||
      (r.payment.transactionReference ?? '').toLowerCase().includes(historySearch.toLowerCase())
    )
    .sort((a, b) => b.payment.commission - a.payment.commission);

  // Toplu ödeme
  const handleAffBulkApprove = async () => {
    const periods = visibleAffRows
      .filter((p) => selectedAffRows.has(affRowKey(p)))
      .map((p) => ({ userId: p.userId, year: p.year, month: p.month }));

    if (periods.length === 0) return;

    setAffBulkSubmitting(true);
    try {
      const res: any = await bulkApproveAffiliatePayments({
        periods,
        transactionReference: affBulkRef || undefined,
        paymentNote: affBulkNote || undefined,
      });
      const failed = (res?.results ?? []).filter((x: any) => !x.success);
      setAffBulkModalOpen(false);
      setAffBulkRef('');
      setAffBulkNote('');
      setSelectedAffRows(new Set());
      fetchAllData();

      if (failed.length > 0) {
        toast.error(`${failed.length} ödeme yapılamadı: ${failed[0].error ?? 'bilinmeyen hata'}`);
      } else {
        toast.success(res?.message ?? 'Ödemeler onaylandı');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Toplu ödeme başarısız oldu.');
    } finally {
      setAffBulkSubmitting(false);
    }
  };

  // Dönemi bir sonraki döneme devret — onay sistem içi modalda alınır (window.confirm değil)
  const handleAffiliateCarryOver = async () => {
    const payment = carryOverModal;
    if (!payment) return;

    const key = `${payment.userId}-${payment.year}-${payment.month}`;
    setCarryingOver(key);
    try {
      const res = await carryOverAffiliatePayment(payment.userId, payment.year, payment.month);
      setCarryOverModal(null);
      toast.success(res.message);
      fetchAllData();
    } catch (err: any) {
      toast.error(err.message || 'Devir işlemi başarısız oldu');
    } finally {
      setCarryingOver(null);
    }
  };

  // Kod Güncelleme
  const handleUpdateCode = async () => {
    if (!selectedCode || !newCode.trim()) return;

    try {
      setIsSubmitting(true);
      await updateReferralCode(selectedCode.id, newCode.trim().toUpperCase());
      toast.success('Kod güncellendi!');
      setShowEditModal(false);
      setNewCode('');
      setSelectedCode(null);
      fetchAllData();
    } catch (err: any) {
      toast.error(err.message || 'Kod güncellenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Kod Silme
  const handleDeleteCode = async () => {
    if (!selectedCode) return;

    try {
      setIsSubmitting(true);
      await deleteReferralCode(selectedCode.id);
      toast.success('Kod silindi!');
      setShowDeleteModal(false);
      setSelectedCode(null);
      fetchAllData();
    } catch (err: any) {
      toast.error(err.message || 'Kod silinemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filtreleme
  const filteredCodes = referralCodes.filter(
    (code) =>
      code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        <span className="ml-3 text-gray-600">Yükleniyor...</span>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchAllData}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Satış Ortaklığı Yönetimi</h1>
        <p className="text-purple-200">Komisyon ödemeleri ve referans kodlarını yönetin</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Bekleyen Ödeme</p>
            <p className="text-2xl font-bold text-amber-600">
              {formatCurrency(stats.totalPendingAmount)}
            </p>
            <p className="text-xs text-gray-400">{stats.totalPendingCount} kullanıcı</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Bu Ay Ödenen</p>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.totalPaidThisMonth)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Toplam Ödenen</p>
            <p className="text-2xl font-bold text-gray-800">
              {formatCurrency(stats.totalPaidAllTime)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Aktif Ortaklar</p>
            <p className="text-2xl font-bold text-purple-600">{stats.activeAffiliates}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Toplam Kod</p>
            <p className="text-2xl font-bold text-blue-600">{stats.totalCodes}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <button
          onClick={() => setActiveTab('payments')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all ${
            activeTab === 'payments'
              ? 'bg-white border border-b-white border-gray-200 text-purple-600 -mb-[1px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4" />
          Bekleyen Ödemeler
          {pendingPayments.length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
              {pendingPayments.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('codes')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all ${
            activeTab === 'codes'
              ? 'bg-white border border-b-white border-gray-200 text-purple-600 -mb-[1px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Key className="w-4 h-4" />
          Referans Kodları
          <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">
            {referralCodes.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all ${
            activeTab === 'history'
              ? 'bg-white border border-b-white border-gray-200 text-purple-600 -mb-[1px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-4 h-4" />
          Ödeme Geçmişi
        </button>
      </div>

      {/* Bekleyen Ödemeler Tab */}
      {activeTab === 'payments' && (
        <>
          {/* DÖNEM KARTLARI — ödeme yönetimiyle aynı düzen */}
          {affPeriodGroups.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-700">Ödeme Dönemleri</h3>
                <span className="text-xs text-gray-400">bir dönem seçin</span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {affPeriodGroups.map((g) => {
                  const active = g.key === selectedAffPeriodKey;
                  return (
                    <button
                      key={g.key}
                      onClick={() => { setSelectedAffPeriodKey(g.key); setSelectedAffRows(new Set()); }}
                      className={`flex-shrink-0 text-left px-4 py-3 rounded-xl border-2 transition-all min-w-[190px] ${
                        active
                          ? 'border-purple-500 bg-purple-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${active ? 'bg-purple-500' : 'bg-gray-300'}`} />
                        <span className={`font-semibold ${active ? 'text-purple-700' : 'text-gray-800'}`}>
                          {g.periodName}
                        </span>
                      </div>
                      <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
                        {formatCurrency(g.totalPayable)}
                      </p>
                      <p className="text-xs text-gray-500">{g.userCount} ortak</p>
                      <p className={`text-xs mt-1 ${
                        !g.windowOpen ? 'text-purple-600 font-medium'
                          : g.isOverdue ? 'text-red-600 font-medium' : 'text-gray-400'
                      }`}>
                        {!g.windowOpen
                          ? `⏳ sırada · ödeme 1-5 ${new Date(g.paymentWindowStart).toLocaleDateString('tr-TR', { month: 'long' })}`
                          : g.isOverdue
                          ? '⏰ ödeme tarihi geçti'
                          : `ödeme: 1-5 ${new Date(g.paymentWindowStart).toLocaleDateString('tr-TR', { month: 'long' })}`}
                      </p>
                      {g.belowMinCount > 0 && (
                        <p className="text-xs text-sky-600 mt-0.5">{g.belowMinCount} tanesi eşik altı</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {pendingPayments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>Bekleyen ödeme bulunmuyor</p>
                <p className="text-sm">Tüm ödemeler tamamlanmış 🎉</p>
              </div>
            ) : activeAffGroup && (
              <>
                <div className="border-b border-gray-200 p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <h3 className="font-semibold text-gray-800">{activeAffGroup.periodName} dönemi</h3>
                    <p className="text-xs text-gray-500">
                      {activeAffGroup.userCount} ortak · toplam {formatCurrency(activeAffGroup.totalPayable)}
                    </p>
                  </div>

                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={affSearch}
                      onChange={(e) => setAffSearch(e.target.value)}
                      placeholder="Ortak ara..."
                      className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-52 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={affHideBelowMin}
                      onChange={(e) => setAffHideBelowMin(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Eşik altını gizle
                  </label>

                  <button
                    onClick={() => setAffBulkModalOpen(true)}
                    disabled={selectedAffRows.size === 0}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    Seçilenleri Öde ({selectedAffRows.size})
                    {selectedAffRows.size > 0 && <span className="opacity-90">· {formatCurrency(selectedAffTotal)}</span>}
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3 w-10">
                          <input type="checkbox" checked={allAffSelected} onChange={toggleAllAff} className="rounded border-gray-300" />
                        </th>
                        <th className="px-4 py-3 font-semibold">Ortak</th>
                        <th className="px-4 py-3 font-semibold text-right">Ödenecek</th>
                        <th className="px-4 py-3 font-semibold text-right">Devir</th>
                        <th className="px-4 py-3 font-semibold text-center">Getirilen</th>
                        <th className="px-4 py-3 font-semibold">Ödeme yöntemi</th>
                        <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibleAffRows.length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Bu dönemde kayıt yok</td></tr>
                      ) : visibleAffRows.map((payment) => {
                        const payable = payment.payableAmount ?? payment.commission;
                        const key = affRowKey(payment);
                        const carryIns = payment.carryIns ?? [];
                        return (
                          <tr key={key} className={`hover:bg-gray-50 ${selectedAffRows.has(key) ? 'bg-purple-50/40' : ''}`}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedAffRows.has(key)}
                                onChange={() => toggleAffRow(payment)}
                                disabled={payable <= 0}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800">{payment.userName}</p>
                              <p className="text-xs text-gray-500">{payment.userEmail}</p>
                              <span className="inline-block mt-1 text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                {payment.referralCode}
                              </span>
                              {carryIns.length > 0 && (
                                <p className="text-[11px] text-sky-700 mt-1">
                                  ↪ {carryIns.map((c) => c.periodName).join(', ')} devredilmişti
                                </p>
                              )}
                              {payment.isBelowMinimum && (
                                <span className="inline-block mt-1 ml-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                  asgari {formatCurrency(payment.minPayoutAmount ?? 50)} altı
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-green-600">
                              {formatCurrency(payable)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-sky-700">
                              {(payment.carryInAmount ?? 0) > 0 ? `+${formatCurrency(payment.carryInAmount!)}` : '—'}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-600">{payment.referredUsersCount} kişi</td>
                            <td className="px-4 py-3">
                              {payment.paymentMethodMasked ? (
                                <span className="text-xs text-gray-600">
                                  {payment.paymentMethodType} · {payment.paymentMethodMasked}
                                </span>
                              ) : (
                                <span className="text-xs text-red-600">⚠️ tanımlı değil</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => { setSelectedPayment(payment); setShowApproveModal(true); }}
                                  disabled={payable <= 0}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-40"
                                >
                                  Öde
                                </button>
                                {payment.isBelowMinimum && (
                                  <button
                                    onClick={() => setCarryOverModal(payment)}
                                    className="px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                                    title="Sonraki döneme ertele"
                                  >
                                    <ArrowRightCircle className="w-3 h-3" />
                                    Devret
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Referans Kodları Tab */}
      {activeTab === 'codes' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="border-b border-gray-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-gray-800">Tüm Referans Kodları</h3>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Kod veya kullanıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm w-64 focus:outline-none focus:border-purple-300"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Referans Kodu</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Sahip</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Oluşturulma</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Getirilen</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Toplam Kazanç</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Durum</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded">
                        {code.code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{code.userName}</p>
                      <p className="text-xs text-gray-500">{code.userEmail}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {code.createdAt ? formatShortDate(code.createdAt) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openReferredUsers(code)}
                        disabled={code.referredCount === 0}
                        className={`inline-flex items-center gap-1 text-sm ${
                          code.referredCount > 0
                            ? 'text-purple-600 hover:underline'
                            : 'text-gray-400 cursor-default'
                        }`}
                        title="Getirilenleri gör"
                      >
                        <span className="font-semibold">{code.referredCount}</span>
                        <span>kişi</span>
                        {code.referredCount > 0 && <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-semibold text-green-600">
                      {formatCurrency(code.totalEarnings)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          code.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {code.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedCode(code);
                            setNewCode(code.code);
                            setShowEditModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                          title="Düzenle"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCode(code);
                            setShowDeleteModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCodes.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Sonuç bulunamadı</p>
            </div>
          )}
        </div>
      )}

      {/* Ödeme Geçmişi Tab */}
      {activeTab === 'history' && (
        <>
          {/* DÖNEM KARTLARI — bekleyen ekranıyla aynı düzen */}
          {affHistoryGroups.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-700">Ödeme Dönemleri</h3>
                <span className="text-xs text-gray-400">bir dönem seçin</span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {affHistoryGroups.map((g) => {
                  const active = g.key === selectedHistoryKey;
                  return (
                    <button
                      key={g.key}
                      onClick={() => setSelectedHistoryKey(g.key)}
                      className={`flex-shrink-0 text-left px-4 py-3 rounded-xl border-2 transition-all min-w-[190px] ${
                        active
                          ? 'border-green-500 bg-green-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={`font-semibold ${active ? 'text-green-700' : 'text-gray-800'}`}>
                          {g.periodName}
                        </span>
                      </div>
                      <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
                        {formatCurrency(g.total)}
                      </p>
                      <p className="text-xs text-gray-500">{g.userCount} ortağa ödendi</p>
                      {g.lastPaidAt && (
                        <p className="text-xs text-gray-400 mt-1">son: {formatShortDate(g.lastPaidAt)}</p>
                      )}
                      {g.carriedCount > 0 && (
                        <p className="text-xs text-sky-600 mt-0.5">{g.carriedCount} devirli ödeme</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {affHistoryGroups.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Henüz ödeme yapılmamış</p>
              </div>
            ) : activeAffHistoryGroup && (
              <>
                <div className="border-b border-gray-200 p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <h3 className="font-semibold text-gray-800">{activeAffHistoryGroup.periodName} ödemesi</h3>
                    <p className="text-xs text-gray-500">
                      {activeAffHistoryGroup.userCount} ortak · toplam {formatCurrency(activeAffHistoryGroup.total)}
                    </p>
                  </div>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Ortak veya referans ara..."
                      className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Ortak</th>
                        <th className="px-4 py-3 font-semibold text-right">Ödenen</th>
                        <th className="px-4 py-3 font-semibold text-right">Devir</th>
                        <th className="px-4 py-3 font-semibold">Ödeme tarihi</th>
                        <th className="px-4 py-3 font-semibold">Referans</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibleAffHistoryRows.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Kayıt bulunamadı</td></tr>
                      ) : visibleAffHistoryRows.map(({ user, payment }) => {
                        const carried = payment.carriedPeriods ?? [];
                        return (
                          <tr key={payment.id} className="hover:bg-gray-50 align-top">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800">{user.userName}</p>
                              <p className="text-xs text-gray-500">{user.userEmail}</p>
                              <span className="inline-block mt-1 text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                {user.referralCode}
                              </span>
                              {carried.length > 0 && (
                                <div className="mt-1.5 p-2 bg-sky-50 border border-sky-200 rounded-lg space-y-0.5 inline-block">
                                  <p className="text-[11px] font-medium text-sky-800">
                                    {carried.length + 1} dönem bu ödemeyle kapandı
                                  </p>
                                  <div className="flex justify-between gap-4 text-[11px] text-gray-600">
                                    <span>{payment.periodName} (ödeme dönemi)</span>
                                    <span className="tabular-nums">{formatCurrency(payment.ownCommission ?? 0)}</span>
                                  </div>
                                  {carried.map((c) => (
                                    <div key={`${c.year}-${c.month}`} className="flex justify-between gap-4 text-[11px] text-sky-700">
                                      <span>{c.periodName} — devredilmişti</span>
                                      <span className="tabular-nums">{formatCurrency(c.commission)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-green-600">
                              {formatCurrency(payment.commission)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-sky-700">
                              {(payment.carryInAmount ?? 0) > 0 ? `+${formatCurrency(payment.carryInAmount!)}` : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-600">{formatShortDate(payment.paidAt)}</td>
                            <td className="px-4 py-3">
                              {payment.transactionReference ? (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono">
                                  {payment.transactionReference}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Ödeme Onay Modal */}
      {showApproveModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Komisyon Ödemesini Onayla</h3>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedPayment(null);
                  setTransactionRef('');
                  setPaymentNote('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800">{selectedPayment.userName}</p>
                <p className="text-xs text-gray-500">{selectedPayment.userEmail}</p>
                <div className="flex justify-between mt-2">
                  <span className="text-gray-600">Dönem:</span>
                  <span className="font-medium">{selectedPayment.periodName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Komisyon:</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency(selectedPayment.payableAmount ?? selectedPayment.commission)}
                  </span>
                </div>
              </div>

              {/* Devirle birlikte kapanacak dönemler */}
              {(selectedPayment.carryIns?.length ?? 0) > 0 && (
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl">
                  <p className="text-sm font-medium text-sky-800 mb-2">
                    Bu ödeme {selectedPayment.carryIns!.length + 1} dönemi birlikte kapatacak:
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-gray-700">
                      <span>{selectedPayment.periodName} (bu dönem)</span>
                      <span className="font-medium">{formatCurrency(selectedPayment.commission)}</span>
                    </div>
                    {selectedPayment.carryIns!.map((c) => (
                      <div key={`${c.year}-${c.month}`} className="flex justify-between text-sm text-sky-700">
                        <span>{c.periodName} — devredilmişti</span>
                        <span className="font-medium">{formatCurrency(c.commission)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İşlem Referans No (opsiyonel)
                </label>
                <input
                  type="text"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-300"
                  placeholder="AFF-2025-0105-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Not (opsiyonel)
                </label>
                <input
                  type="text"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-300"
                  placeholder="Banka transferi yapıldı"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedPayment(null);
                    setTransactionRef('');
                    setPaymentNote('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  İptal
                </button>
                <button
                  onClick={handleApprovePayment}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-green-600 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kod Düzenleme Modal */}
      {showEditModal && selectedCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Referans Kodunu Düzenle</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCode(null);
                  setNewCode('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800">{selectedCode.userName}</p>
                <p className="text-xs text-gray-500">{selectedCode.userEmail}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yeni Kod
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-300 font-mono"
                  placeholder="EBH-NEWCODE"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCode(null);
                    setNewCode('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  İptal
                </button>
                <button
                  onClick={handleUpdateCode}
                  disabled={isSubmitting || !newCode.trim()}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-purple-600 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kod Silme Modal */}
      {showDeleteModal && selectedCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Referans Kodunu Sil</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCode(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-red-700 text-sm">
                  <strong>{selectedCode.userName}</strong> kullanıcısının{' '}
                  <strong className="font-mono">{selectedCode.code}</strong> kodunu silmek
                  istediğinizden emin misiniz?
                </p>
                <p className="text-red-600 text-xs mt-2">
                  Bu işlem geri alınamaz. Kullanıcı yeni kod oluşturabilir.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedCode(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  İptal
                </button>
                <button
                  onClick={handleDeleteCode}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-red-600 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Getirilen Kullanıcılar Modal */}
      {showReferredModal && referredForCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-800">Getirilen Kullanıcılar</h3>
                <p className="text-xs text-gray-500 truncate">
                  <span className="font-mono">{referredForCode.code}</span> · {referredForCode.userName}
                </p>
              </div>
              <button
                onClick={() => { setShowReferredModal(false); setReferredForCode(null); setReferredUsers([]); }}
                className="text-gray-400 hover:text-gray-600 shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              {loadingReferred ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                </div>
              ) : referredUsers.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">Henüz getirilen kullanıcı yok.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {referredUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">{u.fullName}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        {u.phoneNumber && <p className="text-xs text-gray-400">{u.phoneNumber}</p>}
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {u.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatShortDate(u.createdAtUtc)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TOPLU ÖDEME ONAYI */}
      {affBulkModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={affBulkSubmitting ? undefined : () => setAffBulkModalOpen(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Toplu Komisyon Ödemesi</h3>
                <p className="text-sm text-gray-500">{activeAffGroup?.periodName} dönemi</p>
              </div>
              <button
                onClick={() => setAffBulkModalOpen(false)}
                disabled={affBulkSubmitting}
                className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <p className="text-sm text-gray-600">{selectedAffRows.size} ortağa ödenecek</p>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedAffTotal)}</p>
              </div>

              <div className="max-h-48 overflow-y-auto border rounded-xl divide-y divide-gray-100">
                {visibleAffRows
                  .filter((p) => selectedAffRows.has(affRowKey(p)))
                  .map((p) => (
                    <div key={affRowKey(p)} className="px-3 py-2 flex items-center justify-between text-sm">
                      <div>
                        <p className="text-gray-800">{p.userName}</p>
                        {!p.paymentMethodMasked && (
                          <p className="text-[11px] text-red-600">⚠️ ödeme yöntemi tanımlı değil</p>
                        )}
                        {(p.carryIns?.length ?? 0) > 0 && (
                          <p className="text-[11px] text-sky-700">
                            +{p.carryIns!.length} devredilmiş dönem birlikte kapanacak
                          </p>
                        )}
                      </div>
                      <span className="font-medium tabular-nums text-green-700">
                        {formatCurrency(p.payableAmount ?? p.commission)}
                      </span>
                    </div>
                  ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">İşlem Referans No (opsiyonel)</label>
                <input
                  type="text"
                  value={affBulkRef}
                  onChange={(e) => setAffBulkRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Not (opsiyonel)</label>
                <input
                  type="text"
                  value={affBulkNote}
                  onChange={(e) => setAffBulkNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-300"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setAffBulkModalOpen(false)}
                  disabled={affBulkSubmitting}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  onClick={handleAffBulkApprove}
                  disabled={affBulkSubmitting}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {affBulkSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ödeniyor...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      {selectedAffRows.size} Ödemeyi Onayla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devir onayı — tarayıcı kutusu değil, sistemin kendi bildirimi */}
      {carryOverModal && (
        <CarryOverConfirmModal
          userName={carryOverModal.userName}
          userEmail={carryOverModal.userEmail}
          periodName={carryOverModal.periodName}
          amountText={formatCurrency(carryOverModal.payableAmount ?? carryOverModal.commission)}
          minPayoutText={formatCurrency(carryOverModal.minPayoutAmount ?? 50)}
          kindLabel="Satış ortaklığı komisyonu"
          loading={carryingOver !== null}
          onCancel={() => setCarryOverModal(null)}
          onConfirm={handleAffiliateCarryOver}
        />
      )}
    </div>
  );
}